import { EntityRefUtil, FormSubmissionRevisionDTO, isMobileFormSubmissionSource } from '@gearbelt/global';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

import { QuestionIcon } from '@/components/icons/question';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, initials } from '@/lib/utils';

type RevisionHistoryPanelProps = {
  revisions: FormSubmissionRevisionDTO[];
  selectedRevision: number | undefined;
  onSelectRevision: (revision: number) => void;
  isLoading?: boolean;
};

export function RevisionHistoryPanel({ revisions, selectedRevision, onSelectRevision, isLoading = false }: RevisionHistoryPanelProps) {
  return (
    <div className="flex flex-1 flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>Activity</div>
        <Link
          to="https://help.sitemate.com/en/articles/16209846-how-form-versioning-works-in-gearbelt"
          target="_blank"
          className="bg-grey-1 flex w-fit gap-1 rounded p-1 text-sm">
          <QuestionIcon className="h-4 w-4" />
          Learn more
        </Link>
      </div>

      <div className="border-b" />

      {isLoading && <div className="text-grey-5 py-2 text-sm">Loading activity…</div>}

      {!isLoading && revisions.length === 0 && <div className="text-grey-5 py-2 text-sm">No activity yet.</div>}

      {!isLoading && (
        <div className="flex flex-col">
          {revisions.map((revision) => {
            const isSelected = revision.revision === selectedRevision;
            const isOriginal = revision.revision === 0;
            const creatorName = EntityRefUtil.createUserFullNameRef(revision.createdBy, '-');
            const versionLabel = isOriginal ? `Created v${revision.revision + 1}` : `Edited v${revision.revision}`;
            const activityLabel = isMobileFormSubmissionSource(revision.source) ? `${versionLabel} (mobile)` : versionLabel;

            return (
              <button
                key={revision.revision}
                type="button"
                disabled={isOriginal}
                onClick={() => onSelectRevision(revision.revision)}
                aria-pressed={isSelected}
                className={cn('flex justify-between gap-3 rounded p-2 text-left', !isOriginal && 'hover:bg-grey-1 cursor-pointer', isSelected && 'bg-grey-1')}>
                <div className="flex items-start gap-2">
                  <Avatar>
                    {revision.createdBy?.avatarUrl ? <AvatarImage src={revision.createdBy.avatarUrl} /> : null}
                    <AvatarFallback>{initials(creatorName)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-bold">{creatorName}</div>
                    <div className="border-grey-5 text-grey-5 flex h-[25px] items-center border-l-[3px] pl-5 text-xs italic">{activityLabel}</div>
                  </div>
                </div>
                <div className="text-grey-5 text-xs">{formatDistanceToNow(new Date(revision.createdAt), { addSuffix: true })}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
